import type { U3141AnnotatedLotChainIndexMergeDryRunResponse } from '../../../U3141Service';
import { u3141ParseAnnotatedLotChainIndexMergeDryRunResponse } from '../../../tools/u3141AnnotatedLotChainIndexMergeHelpers';
import { useU3141UseFetchAnnotatedLotChainIndexPdfHandlers } from './u3141ChildLotChainTab/U3141UseFetchAnnotatedLotChainIndexPdfHandlers';

export interface U3141UseGenerateAnnotatedLotChainHandlersParams {
  doDryRunFn: (masterLotChainId: string, anchorChildLotChainId: string) => Promise<{ data: unknown }>;
  doLaunchMergeFn: (masterLotChainId: string, anchorChildLotChainId: string) => Promise<{ data: unknown }>;
  onDryRunSuccess?: (result: U3141AnnotatedLotChainIndexMergeDryRunResponse) => void;
  onLaunchSuccess?: (jobId: string) => void;
  onError?: (errors: { error: unknown }) => void;
}

export interface U3141UseGenerateAnnotatedLotChainHandlersReturn {
  dryRunAnnotatedLotChainMerge: (masterLotChainId: string, anchorChildLotChainId: string) => Promise<void>;
  launchAnnotatedLotChainMerge: (masterLotChainId: string, anchorChildLotChainId: string) => Promise<void>;
}

export function useU3141GenerateAnnotatedLotChainHandlers({
  doDryRunFn,
  doLaunchMergeFn,
  onDryRunSuccess,
  onLaunchSuccess,
  onError,
}: U3141UseGenerateAnnotatedLotChainHandlersParams): U3141UseGenerateAnnotatedLotChainHandlersReturn {
  const { fetchAnnotatedLotChainIndexPdf } = useU3141UseFetchAnnotatedLotChainIndexPdfHandlers({
    doFetchFn: doLaunchMergeFn,
    onSuccess: (jobId) => onLaunchSuccess?.(jobId),
    onError,
  });

  const dryRunAnnotatedLotChainMerge = async (masterLotChainId: string, anchorChildLotChainId: string) => {
    try {
      const response = await doDryRunFn(masterLotChainId, anchorChildLotChainId);
      const parsed = u3141ParseAnnotatedLotChainIndexMergeDryRunResponse(response?.data);
      if (!parsed) {
        onError?.({ error: new Error('Invalid dry-run response') });
        return;
      }
      onDryRunSuccess?.(parsed);
    } catch (error) {
      onError?.({ error });
    }
  };

  const launchAnnotatedLotChainMerge = async (masterLotChainId: string, anchorChildLotChainId: string) => {
    await fetchAnnotatedLotChainIndexPdf(masterLotChainId, anchorChildLotChainId);
  };

  return { dryRunAnnotatedLotChainMerge, launchAnnotatedLotChainMerge };
}
